import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  FormControlLabel,
  Stack,
  Alert,
  Switch,
} from '@mui/material';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { importSourcesService } from '../../services/importSourcesService';

const schema = z.object({
  name: z.string().min(1, 'Name is required'),
  url: z.string().url('Must be a valid URL'),
  importIntervalMinutes: z.number().min(5, 'Minimum 5 minutes').max(1440, 'Maximum 1440 minutes'),
  enabled: z.boolean(),
});

type FormData = z.infer<typeof schema>;

interface AddImportSourceDialogProps {
  open: boolean;
  onClose: () => void;
}

export const AddImportSourceDialog: React.FC<AddImportSourceDialogProps> = ({ open, onClose }) => {
  const queryClient = useQueryClient();
  const [error, setError] = useState<string | null>(null);

  const {
    register,
    handleSubmit,
    reset,
    watch,
    setValue,
    formState: { errors },
  } = useForm<FormData>({
    resolver: zodResolver(schema),
    defaultValues: {
      name: '',
      url: '',
      importIntervalMinutes: 60,
      enabled: true,
    },
  });

  const enabled = watch('enabled');

  const createMutation = useMutation({
    mutationFn: importSourcesService.create,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['import-sources'] });
      handleClose();
    },
    onError: (err: any) => {
      setError(err.response?.data?.message || 'Failed to create import source');
    },
  });

  const handleClose = () => {
    reset();
    setError(null);
    onClose();
  };

  const onSubmit = (data: FormData) => {
    setError(null);
    createMutation.mutate(data);
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>Add Import Source</DialogTitle>
      <form onSubmit={handleSubmit(onSubmit)}>
        <DialogContent>
          <Stack spacing={3}>
            {error && <Alert severity="error">{error}</Alert>}

            <TextField
              label="Name"
              {...register('name')}
              error={!!errors.name}
              helperText={errors.name?.message}
              fullWidth
              required
            />

            <TextField
              label="URL"
              {...register('url')}
              error={!!errors.url}
              helperText={errors.url?.message || 'URL of the blacklist source'}
              fullWidth
              required
              placeholder="https://example.com/blacklist.txt"
            />

            <TextField
              label="Import Interval (Minutes)"
              type="number"
              {...register('importIntervalMinutes', { valueAsNumber: true })}
              error={!!errors.importIntervalMinutes}
              helperText={errors.importIntervalMinutes?.message || 'How often to check for updates (5-1440 minutes)'}
              fullWidth
              required
            />

            <FormControlLabel
              control={
                <Switch
                  checked={enabled}
                  onChange={(e) => setValue('enabled', e.target.checked)}
                />
              }
              label="Enabled"
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose}>Cancel</Button>
          <Button type="submit" variant="contained" disabled={createMutation.isPending}>
            Add Source
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
};
